import { useMemo } from "react";
import { FiInfo, FiMessageSquare } from "react-icons/fi";
import { cn } from "utils/helpers";
import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import { SelectOption, SelectWithLabel } from "newui/components/ui/InputWithLabel";
import { AutoSaveIndicator } from "../../../../../../../components/ui/AutoSave/AutoSaveIndicator";
import type { SupplierEvaluationSummary } from "../../../../types/scoring.type";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo";

interface ScoringModalDescriptionBarProps {
	instructionsPanelOpen: boolean;
	isMobile: boolean;
	isSaving: boolean;
	lastSavedAt: Date | null;
	saveError: string | null;
	selectedInviteId: number;
	supplierOptions: SelectOption[];
	suppliers: SupplierEvaluationSummary[];
	onSupplierChange: (option: SelectOption) => void;
	onToggleGuide: () => void;
	onAddClarification?: () => void;
}

/**
 * Modal description-bar for the supplier scoring modal:
 *   [logo] [supplier select | mobile label] [autosave] [scoring-guide toggle]
 *
 * Rendered inside the `description` slot of the outer Modal — distinct from
 * `InitialEvaluationStep/ScoringModalHeader.tsx`, which renders the in-body
 * status / dates / progress section.
 */
export const ScoringModalDescriptionBar: React.FC<ScoringModalDescriptionBarProps> = ({
	suppliers,
	selectedInviteId,
	supplierOptions,
	onSupplierChange,
	isMobile,
	isSaving,
	lastSavedAt,
	saveError,
	instructionsPanelOpen,
	onAddClarification,
	onToggleGuide,
}) => {
	const currentSupplier = suppliers.find((s) => s.inviteId === selectedInviteId);

	// Fix the supplier select to the longest label so the modal layout doesn't
	// shift when the user switches suppliers. `ch` units approximate the "0"
	// width in the current font; the +10ch budget covers react-select's
	// chevron, optional clear button, and internal padding. Clamped between
	// 20–60ch so very short or very long names don't blow out the layout.
	const selectWidth = useMemo(() => {
		const longestLabel = supplierOptions.reduce(
			(max, o) => Math.max(max, o.label.length),
			0
		);
		const targetCh = Math.min(Math.max(longestLabel + 10, 20), 60);
		return `${targetCh}ch`;
	}, [supplierOptions]);

	return (
		<div
			className="ml-4 md:ml-8 flex flex-wrap items-center gap-2 md:gap-4"
			data-testid="scoring-modal-supplier-bar"
		>
			{currentSupplier && (
				<CompanyLogoDisplay className="size-10 md:size-14 shrink-0 rounded-md border border-gray-200 bg-white object-contain p-1" data-testid="scoring-modal-supplier-logo" supplierLogoUrl={currentSupplier.supplierLogo} supplierName={currentSupplier.supplierName} size="custom" displayStyle="normal" />
			)}
			{isMobile ? (
				<span
					className="text-sm font-medium text-gray-900"
					data-testid="scoring-modal-supplier-label"
				>
					Scoring {currentSupplier?.supplierName ?? ""}
				</span>
			) : (
				<SelectWithLabel
					className="mt-3"
					value={selectedInviteId}
					options={supplierOptions}
					onChange={onSupplierChange}
					width={selectWidth}
					data-testid="supplier-select"
				/>
			)}
			<AutoSaveIndicator
				isSaving={isSaving}
				lastSavedAt={lastSavedAt}
				error={saveError}
				size="xs"
			/>
			{onAddClarification && (
				<button
					type="button"
					onClick={onAddClarification}
					className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-100 hover:text-gray-600 shrink-0"
					data-testid="scoring-add-clarification-btn"
				>
					<FiMessageSquare size={14} />
					Add Clarification
				</button>
			)}
			<button
				type="button"
				onClick={onToggleGuide}
				className={cn(
					"flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors shrink-0",
					instructionsPanelOpen
						? "bg-blue-100 text-blue-700 border border-blue-300"
						: "bg-blue-50 text-blue-600 border border-blue-200"
				)}
				data-testid="toggle-instructions-btn"
			>
				<FiInfo size={14} />
				{instructionsPanelOpen ? "Hide Scoring Guide" : "Show Scoring Guide"}
			</button>
		</div>
	);
};
